import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { auditLine, messageTemplateApi } from "../../utils/messageTemplateApi";

const useStyles = makeStyles({
  heading: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#101828",
    marginBottom: "8px",
  },
  cell: { padding: "6px 8px", fontSize: "13px", verticalAlign: "top" },
  touch: { minHeight: "44px" },
  source: { fontFamily: "monospace", fontSize: "12px" },
  templateRow: { backgroundColor: "#f8f9fc" },
  detail: { fontSize: "12px", color: "#475467" },
});

const ACTION_LABELS = {
  BASELINE: "Baseline",
  MIGRATE: "Converted to Mustache",
  CREATE: "Created",
  UPDATE: "Edited",
  DELETE: "Deleted",
  REACTIVATE: "Reactivated",
  RESTORE: "Restored",
  MAP_VARIABLE: "Variable connected",
};

/** Template-wide entries: they apply to every language and carry no content to view. */
const TEMPLATE_ACTION_LABELS = {
  UPDATE: "Template description changed",
  DELETE: "Template deleted (all languages)",
  REACTIVATE: "Template reactivated",
};

/**
 * Revision list for one language of a template, with view and restore. Template-wide changes
 * (description, delete, reactivate) appear here too.
 */
const VersionHistory = ({
  templateId,
  localeId,
  currentRevision,
  active,
  onRestored,
  refreshKey,
}) => {
  const classes = useStyles();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (!templateId || !localeId) return;
    setLoading(true);
    messageTemplateApi
      .versions(templateId, localeId)
      .then((data) => setVersions(data || []))
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [templateId, localeId, refreshKey]);

  const open = (revision) =>
    messageTemplateApi
      .version(templateId, localeId, revision)
      .then(setViewing)
      .catch((e) => toast.error(e.message));

  const restore = async () => {
    setRestoring(true);
    try {
      const restored = await messageTemplateApi.restore(
        templateId,
        localeId,
        viewing.revision,
        currentRevision,
        `Restored revision ${viewing.revision}`,
      );
      toast.success(
        `Revision ${viewing.revision} restored as revision ${restored.revision}.`,
      );
      setViewing(null);
      onRestored(restored);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Box component="section" aria-labelledby="mt-history-heading">
      <Typography
        id="mt-history-heading"
        component="h2"
        className={classes.heading}
      >
        History ({localeId})
      </Typography>
      {loading ? (
        <CircularProgress size={24} aria-label="Loading history" />
      ) : (
        <Table size="small" aria-label="Revisions">
          <TableHead>
            <TableRow>
              <TableCell className={classes.cell}>Rev.</TableCell>
              <TableCell className={classes.cell}>Change</TableCell>
              <TableCell className={classes.cell}>When / who</TableCell>
              <TableCell className={classes.cell}>Note</TableCell>
              <TableCell className={classes.cell}>
                <span className="sr-only">Actions</span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {versions.map((v) =>
              v.templateLevel ? (
                <TableRow
                  key={`template-${v.revision}-${v.createdAt}`}
                  className={classes.templateRow}
                >
                  <TableCell className={classes.cell}>
                    <span className="sr-only">Whole template</span>
                    <span aria-hidden="true">—</span>
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {TEMPLATE_ACTION_LABELS[v.action] ||
                      `Template: ${ACTION_LABELS[v.action] || v.action}`}
                    {v.action === "UPDATE" && v.description && (
                      <div className={classes.detail}>“{v.description}”</div>
                    )}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {auditLine(
                      v.createdAt,
                      v.createdByName ||
                        (v.createdBy ? `user ${v.createdBy}` : "system"),
                    )}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {v.changeNote || "—"}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <span className={classes.detail}>All languages</span>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={`language-${v.revision}`}>
                  <TableCell className={classes.cell}>
                    {v.revision}
                    {v.revision === currentRevision && (
                      <span className="sr-only"> (current)</span>
                    )}
                    {v.revision === currentRevision && " •"}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {ACTION_LABELS[v.action] || v.action}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {auditLine(
                      v.createdAt,
                      v.createdByName ||
                        (v.createdBy ? `user ${v.createdBy}` : "system"),
                    )}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {v.changeNote || "—"}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <Button
                      className={classes.touch}
                      onClick={() => open(v.revision)}
                      aria-label={`View revision ${v.revision}`}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      )}

      <Dialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        maxWidth="lg"
        fullWidth
      >
        {viewing && (
          <>
            <DialogTitle>
              Revision {viewing.revision} —{" "}
              {ACTION_LABELS[viewing.action] || viewing.action}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" gutterBottom>
                {auditLine(viewing.createdAt, viewing.createdByName)}
                {viewing.templateFormat === "PRINTF" && " · legacy %s format"}
              </Typography>
              <TextField
                label="Description"
                value={viewing.description || ""}
                fullWidth
                margin="dense"
                InputProps={{ readOnly: true }}
                variant="outlined"
              />
              <TextField
                label="Subject"
                value={viewing.subject || ""}
                fullWidth
                margin="dense"
                InputProps={{ readOnly: true }}
                variant="outlined"
              />
              <TextField
                label="Body source"
                value={viewing.htmlTemplate || ""}
                fullWidth
                multiline
                minRows={14}
                maxRows={30}
                margin="dense"
                variant="outlined"
                InputProps={{ readOnly: true, className: classes.source }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                className={classes.touch}
                onClick={() => setViewing(null)}
              >
                Close
              </Button>
              <Button
                className={classes.touch}
                color="primary"
                variant="contained"
                onClick={restore}
                disabled={
                  restoring || !active || viewing.revision === currentRevision
                }
                title={
                  !active
                    ? "Reactivate the template and language first"
                    : undefined
                }
              >
                {restoring ? "Restoring…" : "Restore this revision"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

VersionHistory.propTypes = {
  templateId: PropTypes.number,
  localeId: PropTypes.string,
  currentRevision: PropTypes.number,
  active: PropTypes.bool,
  onRestored: PropTypes.func.isRequired,
  refreshKey: PropTypes.number,
};

VersionHistory.defaultProps = {
  templateId: null,
  localeId: null,
  currentRevision: null,
  active: true,
  refreshKey: 0,
};

export default VersionHistory;
